/**
 * **Set role…** (spec §8.11, M18): what the block *is* — one of the punned `km:PlanningRole`
 * classes — stated as a `km:role` link. A block may carry several roles (Q49); this lists the ones it
 * has and adds another. `rdf:type` follows in projection (§13.3), which is the difference between
 * "this block is a position" and "this block is about positions" (§14.7 rule 5).
 */
import { useState } from "react";
import type { TreeID } from "loro-crdt";
import { useTermSearch } from "../api/hooks";
import type { DocHandle } from "../sync/docManager";
import { useLoroSelector } from "../sync/hooks";
import { localName } from "../graph/nodeInfo";
import { Button } from "../ui/Button";
import { Rich, useT } from "../i18n";
import { addRole, PLANNING_ROLE, readRoles, removeRole, sameRoles } from "./roles";

export function RoleDialog({ handle, treeId, userId, readOnly, onClose }: { handle: DocHandle; treeId: TreeID; userId: string; readOnly: boolean; onClose(): void }) {
  const t = useT();
  const roles = useLoroSelector(handle.doc, (d) => readRoles(d, handle.crdtDocId, treeId), sameRoles) ?? [];
  const [q, setQ] = useState("");
  const terms = useTermSearch(q, true, { types: [PLANNING_ROLE], limit: 50 });
  const has = new Set(roles.map((r) => r.iri));
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-[rgba(28,27,26,0.36)] p-8" onClick={onClose}>
      <div className="max-h-full w-96 overflow-auto rounded-lg border border-neutral-200 bg-[var(--surface)] p-5 shadow-[var(--shadow-lg)] dark:border-neutral-700" onClick={(e) => e.stopPropagation()} data-testid="role-dialog">
        <h2 className="mb-1 text-base font-semibold">{t("role.title")}</h2>
        <p className="mb-3 text-xs text-neutral-500">
          <Rich text={t("role.intro")} strong="italic" />
        </p>

        {roles.length > 0 && (
          <ul className="mb-3 space-y-1" data-testid="role-current">
            {roles.map((r) => (
              <li key={r.linkId} className="flex items-center gap-2 rounded border border-neutral-200 px-2 py-1 text-sm dark:border-neutral-800">
                <span className="grow truncate" title={r.iri}>{localName(r.iri)}</span>
                {!readOnly && (
                  <button className="text-xs underline text-neutral-500 hover:text-red-700" data-testid="role-remove" onClick={() => removeRole(handle.doc, treeId, r.linkId)}>
                    {t("common.remove")}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        {!readOnly && (
          <>
            <input
              className="mb-2 w-full kss-control kss-field bg-transparent px-3 py-[7px] text-sm"
              placeholder={t("role.search")}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="role-search"
            />
            <ul className="max-h-64 overflow-auto rounded border border-neutral-200 dark:border-neutral-800" data-testid="role-options">
              {(terms.data ?? []).map((term) => (
                <li key={term.iri}>
                  <button
                    className="block w-full px-2 py-1 text-left text-sm hover:bg-neutral-100 disabled:opacity-40 dark:hover:bg-neutral-800"
                    disabled={has.has(term.iri)}
                    data-testid="role-option"
                    onClick={() => addRole(handle.doc, handle.crdtDocId, treeId, term.iri, userId)}
                  >
                    <span className="font-medium">{term.label}</span> <span className="text-xs text-neutral-500">{term.iri}</span>
                    {term.definition && <div className="text-xs text-neutral-500">{term.definition.slice(0, 120)}</div>}
                  </button>
                </li>
              ))}
              {terms.isError && <li className="p-2 text-sm text-neutral-500">{t("role.offline")}</li>}
            </ul>
          </>
        )}

        <div className="mt-4 flex justify-end">
          <Button variant="ghost" onClick={onClose}>{t("common.close")}</Button>
        </div>
      </div>
    </div>
  );
}
